import * as yup from 'yup';
import _isNil from 'lodash/isNil';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import _map from 'lodash/map';
import _values from 'lodash/values';
import _omit from 'lodash/omit';

import { Row, RenderConfig, TextInput, DateRangeInput, getBasicDateRangeValidation, SelectInput } from 'commons-ui';
import { GAME_NAMES, GAME_NAME_LABLES, GAME_NAME_OPTIONS } from 'pages/constants';
import { FORM_FIELD_KEYS, SUNSET_GAME_NAMES, TOURNAMENT_TYPE_OPTIONS } from './constants';

export const validationSchema = yup.object().shape(
	{
		tournamentID: yup.string().when(['dateRange', 'pwfInviteId'], {
			is: (dateRange: Date[], pwfInviteId: string) => (!dateRange || dateRange.length === 0) && _isEmpty(pwfInviteId),
			then: yup.string().required('Please Enter Tournament ID, pwfInviteId or a date Range'),
			otherwise: yup.string().notRequired()
		}),
		pwfInviteId: yup.string().when(['dateRange', 'tournamentType'], {
			is: (dateRange: Date[], tournamentType: string) => (!dateRange || dateRange.length === 0) && !_isEmpty(tournamentType),
			then: yup.string().required('Please Enter Tournament ID, pwfInviteId or a date Range'),
			otherwise: yup.string().notRequired()
		}),
		dateRange: yup.lazy(() => {
			return yup.array().when(['tournamentID', 'pwfInviteId'], {
				is: (tournamentID: string, pwfInviteId: string) => _isEmpty(tournamentID) && _isEmpty(pwfInviteId),
				then: getBasicDateRangeValidation({
					customMessages: { case1: 'Please Enter Tournament ID, pwfInviteId or a date Range' }
				}).test({
					message: 'Date Range should be maximum of two day',
					test: (value: any) => value[1] - value[0] <= 86400000
				}),
				otherwise: yup.array().notRequired()
			});
		}),
		tournamentType: yup.lazy(() => {
			return yup.string().when(['pwfInviteId'], {
				is: (pwfInviteId: string) => !_isEmpty(pwfInviteId),
				then: yup.string().required('Please Select Tournament Type'),
				otherwise: yup.string()
			});
		})
	},
	[['dateRange', 'tournamentID', 'tournamentType', 'pwfInviteId']]
);

const dateRangeDepResolver = (props: any, dependencyValues: any) => {
	const tournamentID: string = _get(dependencyValues, '0');
	const pwfInviteId: string = _get(dependencyValues, '1');
	const { disabled } = props;
	if (_isEmpty(pwfInviteId) && _isEmpty(tournamentID) && disabled) {
		return { disabled: false };
	}
	if ((!_isEmpty(pwfInviteId) || !_isEmpty(tournamentID)) && !disabled) {
		return { disabled: true };
	}
	return null;
};

const tournamentIdDepResolver = (props: any, dependencyValues: any) => {
	const dateRange: Date[] = _get(dependencyValues, '0');
	const pwfInviteId: string = _get(dependencyValues, '1');
	const { disabled } = props;

	if (_isEmpty(pwfInviteId) && (_isNil(dateRange) || dateRange.length === 0) && disabled) {
		return { disabled: false };
	}
	if ((!_isEmpty(pwfInviteId) || (!_isNil(dateRange) && dateRange.length === 2)) && !disabled) {
		return { disabled: true };
	}

	return null;
};

const tournamentTypeDepResolver = (props: any, dependencyValues: any) => {
	const tournamentID: string = _get(dependencyValues, '0');
	const { disabled } = props;
	if (_isEmpty(tournamentID) && disabled) {
		return { disabled: false };
	}
	if (!_isEmpty(tournamentID) && !disabled) {
		return { disabled: true };
	}
	return null;
};

const pwfInviteIdDepResolver = (props: any, dependencyValues: any) => {
	const tournamentID: string = _get(dependencyValues, '0');
	const dateRange: Date[] = _get(dependencyValues, '1');
	const tournamentType: string = _get(dependencyValues, '2');
	const { disabled } = props;
	if (_isEmpty(tournamentType) && disabled) {
		return { disabled: true };
	}
	if (!_isEmpty(tournamentType) && !disabled) {
		return { disabled: false };
	}

	if (_isEmpty(tournamentID) && (_isNil(dateRange) || dateRange.length === 0) && disabled) {
		return { disabled: false };
	}
	if ((!_isEmpty(tournamentID) || (!_isNil(dateRange) && dateRange.length === 2)) && !disabled) {
		return { disabled: true };
	}

	return null;
};

export const FORM_FIELD_CONFIG = {
	[FORM_FIELD_KEYS.GAME_NAME]: new RenderConfig()
		.setName('gameName')
		.setLabel('Game Name')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true,
			options: _map(_values(_omit(GAME_NAMES, SUNSET_GAME_NAMES)), (gameName) => ({
				id: _get(GAME_NAME_OPTIONS, gameName),
				label: _get(GAME_NAME_LABLES, gameName)
			}))
		}),
	[FORM_FIELD_KEYS.TOURNAMENT_ID]: new RenderConfig()
		.setName('tournamentID')
		.setLabel('Tournament ID')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			dependencies: ['dateRange', 'pwfInviteId'],
			dependencyResolver: tournamentIdDepResolver
		}),
	[FORM_FIELD_KEYS.DATE_RANGE]: new RenderConfig()
		.setName('dateRange')
		.setLabel('Date Range')
		.setRenderer(DateRangeInput)
		.setRenderProps({
			placeholder: 'from-To date',
			dependencies: ['tournamentID', 'pwfInviteId'],
			dependencyResolver: dateRangeDepResolver
		}),
	[FORM_FIELD_KEYS.TOURNAMENT_TYPE]: new RenderConfig()
		.setName('tournamentType')
		.setLabel('Tournament Type')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: TOURNAMENT_TYPE_OPTIONS,
			dependencies: ['tournamentID'],
			dependencyResolver: tournamentTypeDepResolver
		}),
	[FORM_FIELD_KEYS.PWF_INVITE_ID]: new RenderConfig()
		.setName('pwfInviteId')
		.setLabel('PWF Invite Id')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			dependencies: ['tournamentID', 'dateRange', 'tournamentType'],
			dependencyResolver: pwfInviteIdDepResolver
		})
};

export const FORM_LAYOUT_CONFIG = [
	new Row(FORM_FIELD_KEYS.GAME_NAME),
	new Row(FORM_FIELD_KEYS.TOURNAMENT_ID),
	new Row(FORM_FIELD_KEYS.DATE_RANGE),
	new Row(FORM_FIELD_KEYS.TOURNAMENT_TYPE),
	new Row(FORM_FIELD_KEYS.PWF_INVITE_ID)
];
